import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

type Props = {
  open: boolean;
  email: string;
  role: string;
  error: string;
  pending: boolean;
  onEmailChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
};

export default function ProjectAddMemberDialog({ open, email, role, error, pending, onEmailChange, onRoleChange, onCancel, onSubmit }: Props) {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const preventPendingDismissal = (event: Event) => {
    if (pending) event.preventDefault();
  };
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !pending) onCancel(); }}>
      <DialogContent dir={i18n.dir()} showCloseButton={false} className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[400px]"
        onOpenAutoFocus={(event) => { event.preventDefault(); inputRef.current?.focus(); }}
        onCloseAutoFocus={(event) => { event.preventDefault(); }}
        onEscapeKeyDown={preventPendingDismissal} onInteractOutside={preventPendingDismissal}>
        <header className="shrink-0 border-b p-4">
          <DialogTitle>{t("projects.addTeamMember")}</DialogTitle>
          <DialogDescription className="sr-only">{t("common.emailAddress")} — {t("projects.accessLevel")}</DialogDescription>
        </header>
        <form className="flex min-h-0 flex-col" onSubmit={(event) => { event.preventDefault(); if (!pending) onSubmit(); }}>
          <div className="min-h-0 space-y-4 overflow-y-auto p-4">
            {error ? <p id="add-member-error" role="alert" className="text-sm text-destructive dark:text-destructive-foreground">{error}</p> : null}
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="add-member-email">{t("common.emailAddress")}</label>
              <Input ref={inputRef} id="add-member-email" type="email" dir="ltr" value={email} disabled={pending} aria-invalid={Boolean(error)} aria-describedby={error ? "add-member-error" : undefined} onChange={(event) => onEmailChange(event.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="add-member-role">{t("projects.accessLevel")}</label>
              <Select dir={i18n.dir()} value={role} onValueChange={onRoleChange} disabled={pending}>
                <SelectTrigger id="add-member-role" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("aiCatalog.admin")}</SelectItem>
                  <SelectItem value="modifier">{t("aiCatalog.editor")}</SelectItem>
                  <SelectItem value="viewer">{t("aiCatalog.viewer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t p-4">
            <Button type="button" variant="ghost" disabled={pending} onClick={onCancel}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={pending || !email.trim()}>{pending ? <Spinner className="size-4 motion-reduce:animate-none" /> : null}{pending ? t("common.adding") : t("projects.addNewMember")}</Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
